import { IReferralResponse } from "@finbackoffice/clientbff-client";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ClientBFFContext, RuntimeConfigContext } from "../providers";

type IReferralUserSlugState = {
    slug: string | null;
    loading: boolean;
};

const REFERRAL_PARAM_NAME = "ref";

export const useFindReferral = () => {
    const client = useContext(ClientBFFContext);
    const { DOMAIN } = useContext(RuntimeConfigContext);
    const [referralLink, setReferralLink] = useState<string | null>(null);
    const [referralUserSlugState, setReferralUserSlugState] = useState<IReferralUserSlugState>({
        slug: "",
        loading: false,
    });

    const createReferralLink = useCallback(
        (slug: string) => {
            return `https://${DOMAIN}/?${REFERRAL_PARAM_NAME}=${encodeURIComponent(slug)}`;
        },
        [DOMAIN],
    );

    const findReferral = useCallback(async () => {
        setReferralUserSlugState((state) => ({
            ...state,
            loading: true,
        }));
        let response: IReferralResponse = {} as IReferralResponse;

        try {
            response = await client.findReferral();
        } catch (_error: unknown) {
            setReferralUserSlugState((state) => ({
                ...state,
                slug: null,
                loading: false,
            }));
        } finally {
            if (response?.slug) {
                setReferralUserSlugState((state) => ({
                    ...state,
                    slug: response.slug,
                    loading: false,
                }));
                setReferralLink(createReferralLink(response.slug));
            }
        }
    }, [client, createReferralLink]);

    useEffect(() => {
        findReferral();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return useMemo(
        () => ({ ...referralUserSlugState, referralLink, findReferral, createReferralLink }),
        [referralUserSlugState, referralLink, findReferral, createReferralLink],
    );
};

export default useFindReferral;
